import React, {useEffect, useState, forwardRef, useCallback, useRef} from 'react';
import BaseModal from './BaseModal';
import {ToolConfigurationNotesAPI} from '../restAPI/ToolsIntegrationAPI';
import _ from 'lodash';
import { Avatar, Tooltip, Empty, Button } from 'antd';
import { CellRenderers } from 'utils/tableUtils'
import AppLoader from './AppLoader';
import { MODAL_SIZE_MEDIUM } from 'constants/Layout';
import NotesComponent from 'components/NotesComponent';
import moment from 'moment';
import classnames from 'classnames';
import ToolConfigurationNotesPopup from 'components/ToolConfigurationNotesPopup';
import ModalUtil from 'utils/modalUtil';
import parseHtml from 'utils/parserUtil';
import { transformComment } from 'utils/commentUtil';

const ToolConfigurationNotes = forwardRef(({toolConfigId, activeNoteId = undefined, afterClose, users = []}, ref) => {
    const [notesList, setNotesList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [hasMore, setHasMore] = useState(activeNoteId!==undefined);
    const totalPagesRef = useRef(0);
    const loadingNextRef = useRef(true);

    const loadMore = useCallback(() => {
        loadingNextRef.current = true;
        setPage((prevPage) => prevPage + 1);
    }, []);

    const fetchNotesList = useCallback(() => {
        const { cancel, promise } =
            ToolConfigurationNotesAPI.getToolConfigurationNotes(
                toolConfigId,
                page
            );
        const cancelPromise = cancel;
        promise
            .then(({ content, totalPages, last }) => {
                totalPagesRef.current = totalPages;
                loadingNextRef.current = false;
                
                if(last){
                    setHasMore(false);
                }

                if(activeNoteId && hasMore){
                    if (content.some(item => item.id === activeNoteId)) {
                        // Stop fetching if noteId is found
                        setHasMore(false);
                    }
                    else{
                        loadMore();
                    }
                }

                setNotesList((prevData) => {
                    return _.uniqBy([...prevData, ...content], "id");
                });
            })
            .catch((err) => {
                console.log(err);
                loadingNextRef.current = false;
            })
            .finally(() => {
                setLoading(false);
            });
        return () => cancelPromise();
    }, [activeNoteId, page, toolConfigId, loadMore, hasMore]);


    useEffect(() => {
        fetchNotesList();
    }, [fetchNotesList]);


    function getItemDataProps(noteItem) {
        const userName = CellRenderers.VALUE_OR_NA(noteItem.createdBy);
        const matches = userName.match(/\b(\w)/g);

        const noteContent = parseHtml(noteItem.content, {
            transform: (reactNode, node, index) =>  transformComment(reactNode, node, index, users)
        });

        return {
            author: userName,
            avatar: <Avatar size="small">{matches?.join("") || userName[0]}</Avatar>,
            content: <>
                {CellRenderers.VALUE_OR_NA(noteContent)}
            </>,
            datetime: (
                <Tooltip title={moment(noteItem.createdAt).fromNow()} placement="right">
                    {CellRenderers.TIME(noteItem.createdAt)}
                </Tooltip>
            )
        };
    }

    const baseModalProps = {
        title: `Notes`,
        width: MODAL_SIZE_MEDIUM,
        footer: null,
    }


    const onAddNote = useCallback(async () => {
        ModalUtil.show({
            content: (
                <ToolConfigurationNotesPopup
                    okText={'Add'}
                    title={'Add Note'}
                    type={undefined}
                    note
                    noteLength={1000}
                    users = {users}
                    onOk={async (data) => {
                        await ToolConfigurationNotesAPI.createToolConfigurationNote(
                            toolConfigId, data
                        ).promise;
                        fetchNotesList();
                    }}
                />
            ),
        });
    }, [toolConfigId, fetchNotesList, users]);

    return(
        <BaseModal
            ref={ref}
            modalProps={baseModalProps}
            afterClose={afterClose}
        >
            {loading ? <AppLoader /> : _.isEmpty(notesList) ? <Empty description={"No Notes Available"} /> :
                <>
                    <NotesComponent
                        notesList={notesList}
                        getItemDataProps={getItemDataProps}
                        setNotesList={setNotesList}
                        activeNoteId={activeNoteId}
                        users={users}
                        toolConfigId={toolConfigId}
                    />
                    {
                        // plus 1, because the page starts from 0
                        // loadingNextRef is added inorder to show the loading progress of the last page and
                        // then make it disappear
                        (loadingNextRef.current || ((page + 1) < totalPagesRef.current)) && (
                            <div style={{ textAlign: 'center', margin: '16px 0' }}>
                                <Button type='link' onClick={loadMore} loading={loadingNextRef.current}>Load More</Button>
                            </div>
                        )
                    }
                </>
            }
            <Button
                id="plus-more"
                size={30}
                type="primary"
                className={classnames('text-bold more-button')}
                onClick={onAddNote}
            >
                + Add Note
            </Button>
        </BaseModal>
    )
})
export default ToolConfigurationNotes;
